/* global Ecwid */
/* eslint-disable react/jsx-curly-newline */
import { useEffect, useRef, useState, useContext } from 'react'
import { createUseStyles } from 'react-jss'
import { useSelector, shallowEqual } from 'react-redux'
import usePrevious from '@/hooks/usePrevious'
import gsap from 'gsap'
import { Context as LoadingContext } from '@/context/loading'
import { Context as ScrollbarContext } from '@/context/scrollbar'

import style from './style'

const useStyles = createUseStyles(style)

const ShopToastNotification = () => {
  const classes = useStyles()
  const $notification = useRef(null)
  const $progressBar = useRef(null)
  const [cart, setCart] = useState(null)
  const [lastProductAdded, setLastProductAdded] = useState(null)
  const prevCart = usePrevious(cart)
  const { ready } = useContext(LoadingContext)
  const { scrollbar } = useContext(ScrollbarContext)

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { strings, productList } = useSelector((state) => ({
    strings: state.language.string,
    productList: state.shop.items || [],
  }), shallowEqual)

  useEffect(() => {
    // Ecwid Cart EventListener
    if (Ecwid) {
      Ecwid.OnCartChanged.add((c) => {
        setCart(c)
      })
    }
  }, [productList])

  useEffect(() => {
    if (!cart) return
    let productAddedID
    if (!prevCart || prevCart.items.length < cart.items.length) {
      productAddedID = cart.items[cart.items.length - 1]?.product.id
    } else if (prevCart.items.length === cart.items.length) {
      for (let i = 0; i < prevCart.items.length; i += 1) {
        if (prevCart.items[i].quantity < cart.items[i].quantity) {
          productAddedID = cart.items[i].product.id
          break
        }
      }
    }

    const productAdded = productList.find((p) => p.id === productAddedID)
    if (productAdded) {
      setLastProductAdded(productAdded)
    }
  }, [cart, prevCart, productList])

  useEffect(() => {
    if (!cart) return
    if (ready && prevCart?.productsQuantity < cart.productsQuantity) {
      gsap.killTweensOf($notification.current)
      gsap.killTweensOf($progressBar.current)
      gsap.set($notification.current, {
        opacity: 0,
        x: 300,
        duration: 0.5,
        ease: 'power3.out',
      })
      gsap.to($notification.current, {
        opacity: 1,
        pointerEvents: 'all',
        x: 0,
        duration: 1,
        ease: 'power3.out',
        delay: 0.1,
        onStart: () => {
          gsap.fromTo($progressBar.current, {
            scaleX: 1,
          },
          {
            scaleX: 0,
            duration: 4.5,
            ease: 'linear',
            onComplete: () => {
              gsap.to($notification.current, {
                opacity: 0,
                pointerEvents: 'none',
                x: 300,
                duration: 0.5,
                ease: 'power3.out',
              })
            },
          })
        },
      })
    }
  }, [cart, prevCart, ready])

  const handleCartClick = () => {
    if (window.Ecwid) {
      window.Ecwid.Cart.gotoCheckout()
      scrollbar.destroy()
      window.Ecwid.OnPageSwitch.add((e) => {
        if (e.type === 'CATEGORY') scrollbar.init()
      })
      window.scrollbar = scrollbar
    }
  }

  return (
    <div className={classes.toastWrapper}>
      <div className={classes.wrap}>
        <div className={classes.notification} ref={$notification} onClick={handleCartClick} role="presentation">
          <figure className={classes.figure}>
            {lastProductAdded && <img src={lastProductAdded?.image} alt={lastProductAdded?.title} />}
          </figure>
          <div className={classes.textWrapper}>
            <p className="message">
              {lastProductAdded?.title}
              {' '}
              {strings.added_to_cart_label}
            </p>
            <p className="designer">
              {lastProductAdded?.designer}
            </p>
          </div>
          <div className={classes.progressBar} ref={$progressBar} />
        </div>
      </div>
    </div>
  )
}

export default ShopToastNotification
